import {
  ColumnTypes,
  editableComponents,
  IEditableCellProps,
} from "@/containers/ProjectTemplate";
import { nameof } from "@/helpers";
import {
  IFormFieldOptions,
  IMilestoneApprover,
  IMilestoneTemplate,
} from "@/interfaces";
import { useGetFormLookupsQuery, useGetProjectRoleQuery } from "@/services";
import { FieldType } from "@/types";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faPlusCircle, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Table } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { v4 } from "uuid";
import {
  clearRoleDependancies,
  filterRegionAndFunctionalArea,
  isValidRole,
} from "../helpers";

interface IProps {
  milestoneTemplate: IMilestoneTemplate;
  updateMilestone(data: Partial<IMilestoneTemplate>): void;
  setIsValid(isValid: object): void;
}

export const TemplateApprovers = ({
  milestoneTemplate,
  updateMilestone,
  setIsValid,
}: IProps) => {
  const intl = useIntl();
  const { data: { intake } = {} } = useGetFormLookupsQuery();
  const { data: roles } = useGetProjectRoleQuery({});
  const [approvers, setApprovers] = useState<IMilestoneApprover[]>([]);
  const [roleOptions, setRoleOptions] = useState<IFormFieldOptions>({});
  useState<IFormFieldOptions>({});

  useEffect(() => {
    if (!roles) return;
    const dictionary: IFormFieldOptions = {};
    roles?.forEach(
      ({
        id,
        name,
        isActive,
        isDeleted,
      }: {
        id: string;
        name: string;
        isActive: boolean;
        isDeleted: boolean;
      }) => {
        if (isActive && !isDeleted) {
          dictionary[id] = { id: "", fallback: name };
        }
      }
    );

    setRoleOptions(dictionary);
  }, [roles]);

  useEffect(() => {
    if (!milestoneTemplate) return;
    if (!roles) return;
    if (!milestoneTemplate.approvers) return;

    const filteredApprovers = milestoneTemplate.approvers.map((x) =>
      filterDependancies(x)
    );
    setApprovers(filteredApprovers);
  }, [roles, milestoneTemplate]);

  const updateModalState = (updatedApprovers: IMilestoneApprover[]): void => {
    isValidApprovers(updatedApprovers);
    setApprovers(updatedApprovers);
    updateMilestone({ approvers: updatedApprovers });
  };

  const isValidApprovers = (updatedApprovers: IMilestoneApprover[]): void => {
    setIsValid((prev: object) => ({
      ...prev,
      approver: isValidRole(updatedApprovers),
    }));
  };

  const handleAdd = (): void => {
    const newApprover = [
      {
        id: v4(),
      } as IMilestoneApprover,
    ];

    const updatedApprovers = newApprover.concat(approvers);
    updateModalState(updatedApprovers);
  };

  const handleDelete = (id: string): void => {
    const updatedApprovers = approvers.filter((approver) => approver.id !== id);

    updateModalState(updatedApprovers);
  };

  const handleUpdate = (row: IMilestoneApprover): void => {
    const updatedApprovers = [...approvers];
    const index = updatedApprovers.findIndex(
      (approver) => row.id === approver.id
    );
    const item = updatedApprovers[index];
    const updatedRow = clearRoleDependancies(roles, item, row);
    const filteredRow = filterDependancies(updatedRow);

    updatedApprovers.splice(index, 1, {
      ...item,
      ...filteredRow,
    });

    updateModalState(updatedApprovers);
  };

  const filterDependancies = (
    approver: IMilestoneApprover
  ): IMilestoneApprover => {
    if (!roles) return approver;
    if (!approver) return approver;

    const filtered = filterRegionAndFunctionalArea(roles, intake, approver);

    const updatedApprover = { ...approver };

    if (filtered.regions) {
      updatedApprover.regionOptions = filtered.regions;
    }

    if (filtered.functionalAreas) {
      updatedApprover.functionalAreaOptions = filtered.functionalAreas;
    }

    return updatedApprover;
  };

  const defaultColumns: (ColumnTypes[number] & {
    type: FieldType | undefined;
    dataIndex: string;
    required?: boolean;
    editable?: boolean;
    options?: IFormFieldOptions;
    optionsIndex?: string;
    showSearch?: boolean;
    placeholder?: string;
  })[] = [
    {
      title: intl.formatMessage({ id: "milestoneTemplates.columns.role" }),
      dataIndex: nameof<IMilestoneApprover>("roleId"),
      key: "role",
      width: "300px",
      type: "DropDown",
      required: true,
      editable: true,
      showSearch: true,
      options: roleOptions,
    },
    {
      title: intl.formatMessage({ id: "milestoneTemplates.columns.region" }),
      dataIndex: nameof<IMilestoneApprover>("region"),
      key: "region",
      width: "150px",
      type: "DropDown",
      required: true,
      editable: true,
      optionsIndex: nameof<IMilestoneApprover>("regionOptions"),
    },
    {
      title: intl.formatMessage({
        id: "milestoneTemplates.columns.functionalArea",
      }),
      dataIndex: nameof<IMilestoneApprover>("functionalArea"),
      key: "functionalArea",
      width: "175px",
      type: "DropDown",
      required: true,
      editable: true,
      optionsIndex: nameof<IMilestoneApprover>("functionalAreaOptions"),
    },
    {
      title: intl.formatMessage({
        id: "milestoneTemplates.columns.productCategory",
      }),
      dataIndex: nameof<IMilestoneApprover>("defaultForProductCategories"),
      key: "productCategory",
      width: "500px",
      type: "MultiSelectDropDown",
      editable: true,
      options: intake?.productCategory,
      placeholder: intl.formatMessage({ id: "general.all" }),
    },
    {
      title: intl.formatMessage({
        id: "milestoneTemplates.columns.notApplicable",
      }),
      dataIndex: nameof<IMilestoneApprover>("canSetNotApplicable"),
      key: "notApplicable",
      width: "85px",
      type: "Checkbox",
      editable: true,
    },
    {
      title: (
        <Button
          data-test-id="milestone-template-approvers-add"
          type="link"
          onClick={handleAdd}
          icon={<FontAwesomeIcon icon={faPlusCircle as IconProp} size="xl" />}
        />
      ),
      dataIndex: nameof<IMilestoneApprover>("id"),
      key: "delete",
      editable: false,
      type: undefined,
      render: (id: string) => (
        <Button
          data-test-id="milestone-template-approvers-delete"
          type="link"
          onClick={(): void => handleDelete(id)}
          icon={<FontAwesomeIcon icon={faTrash as IconProp} color="red" />}
        />
      ),
    },
  ];

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: IMilestoneApprover) =>
        ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          type: col.type,
          required: col.required,
          options: col.options,
          optionsIndex: col.optionsIndex,
          showSearch: col.showSearch,
          handleSave: handleUpdate,
          placeholder: col.placeholder,
        } as IEditableCellProps<IMilestoneApprover>),
    };
  });

  return (
    <Table
      components={editableComponents}
      style={{ margin: "-12px 0px" }}
      tableLayout="fixed"
      className="w-full"
      size="small"
      columns={columns as ColumnTypes}
      dataSource={approvers}
      rowKey="id"
      pagination={false}
    />
  );
};
